// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Row,
  Select,
  Tooltip
} from "antd";
import {
  dateFormat,
  FIELD_LENGTH,
  formItemBlockLayout,
  NEWS_ARCHIVED,
  NEWS_PRIORITY,
  NEWS_WORKFLOW_STATUS,
  orderDescend
} from "../../../../utils/Constant";
import { SearchOutlined } from "@ant-design/icons";
import {
  KeywordInfoResponseDTO,
  NewsSearchRequestDTO,
  NewsThemeInfoResponseDTO
} from "../../../../api/backend-openapi";
import FormField from "../../../components/FormField";
import { useStore } from "effector-react";
import { NewsThemeStore } from "../../../../state/newsTheme/NewsThemeStore";
import { NewsSearchFormProps } from "../../../types/NewsTypes";
import { requestGetNewsList } from "../../../../state/news/NewsEffects";
import "moment/locale/fr";
import locale from "antd/es/date-picker/locale/fr_FR";
import { requestGetNewsThemes } from "../../../../state/newsTheme/NewsThemeEffects";
import { setNewsThemes } from "../../../../state/newsTheme/NewsThemeEvents";
import { BranchesStore } from "../../../../state/branches/BranchesStore";
import { displayError } from "../../../../utils/displayError";
import { KeywordStore } from "../../../../state/keyword/KeywordStore";

const NewsListSearchForm: React.FunctionComponent<NewsSearchFormProps> = (
  props: NewsSearchFormProps
) => {
  const {
    pagination,
    dataHasLoaded,
    setDataHasLoaded,
    onSearchDtoUpdate
  } = props;
  const { t } = useTranslation();

  const branchList = useStore(BranchesStore);

  const [branch, setBranch] = useState<string | undefined>(undefined);

  const [newsThemeId, setNewsThemeId] = useState<string | undefined>(undefined);

  // call getNewsTheme on page load
  useEffect(() => {
    requestGetNewsThemes();
  }, []);

  useEffect(() => {
    return requestGetNewsThemes.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setNewsThemes(result.data);
        setDataHasLoaded(true);
      } else {
        displayError("news_themes.load_failed");
      }
    });
  });

  const handleSubmit = (values: any) => {
    setDataHasLoaded(false);
    const searchCriteria: NewsSearchRequestDTO = {
      priority: values.priority,
      title: values.title,
      keyword: values.keyword,
      branch: values.branch,
      newsThemeId: values.newsThemeId,
      creationDateStart: values.creationRange
        ? values.creationRange[0].format("YYYY-MM-DD")
        : undefined,
      creationDateEnd: values.creationRange
        ? values.creationRange[1].format("YYYY-MM-DD")
        : undefined,
      status: values.status,
      archived: values.archived !== null ? values.archived : undefined,
      id: values.id
    };
    onSearchDtoUpdate(searchCriteria);
    requestGetNewsList({
      pagination,
      sorter: {
        columnKey: "creationDate",
        order: orderDescend
      },
      dto: searchCriteria
    });
  };

  const { RangePicker } = DatePicker;

  const newsThemeStore: NewsThemeInfoResponseDTO[] | null = useStore(
    NewsThemeStore
  );

  const keywordsStore: KeywordInfoResponseDTO[] | null = useStore(KeywordStore);

  const getKeywordOptions = () => {
    return keywordsStore.map((keyword: KeywordInfoResponseDTO) => ({
      label: keyword.text,
      value: keyword.id
    }));
  };

  return (
    <Form
      {...formItemBlockLayout}
      onFinish={handleSubmit}
      fields={[
        {
          name: "newsThemeId",
          value: newsThemeId
        }
      ]}
    >
      <div className="d-flex flex-row justify-content-end align-items-stretch flex-wrap">
        <div className="flex-grow-1 d-flex align-items-center">
          <Divider orientation="left" className="mb-0 mb-sm-3 mt-3">
            <h3 className="text-secondary mb-0">
              {t("news_list.search_title")}
            </h3>
          </Divider>
        </div>
        <div className="d-flex align-items-center divider-sm-transparent">
          <Divider orientation="right" className="mt-2 mt-sm-3 mb-3">
            <Button
              type="default"
              className="btn-tertiary"
              htmlType="submit"
              loading={!dataHasLoaded}
            >
              <SearchOutlined /> {t("news.global_buttons.search")}
            </Button>
          </Divider>
        </div>
      </div>
      <Row>
        <Col xs={24} sm={9} lg={6}>
          <Form.Item
            name="branch"
            key="branch"
            label={<span>{t("add_news.branch.select")}</span>}
            rules={[]}
          >
            <Select
              id="branch"
              onChange={(value: string) => {
                setBranch(value);
                if (!!value) {
                  setNewsThemeId(undefined);
                }
              }}
              placeholder={t("add_news.branch.placeholder")}
              allowClear
            >
              {Object.keys(branchList).map(key => (
                <Select.Option key={key} value={key}>
                  {branchList[key].label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={9} lg={6}>
          <Form.Item
            name="newsThemeId"
            key="newsThemeId"
            label={<span>{t("add_news.theme.select")}</span>}
            rules={[]}
          >
            <Select
              id="newsThemeId"
              onChange={(value: string) => setNewsThemeId(value)}
              placeholder={t("add_news.theme.placeholder")}
              allowClear
            >
              {newsThemeStore
                .filter(
                  (newsTheme: NewsThemeInfoResponseDTO) =>
                    !branch || newsTheme.branch === branch
                )
                .map((newsTheme: NewsThemeInfoResponseDTO) => {
                  const label = branch
                    ? newsTheme.name
                    : `${branchList[newsTheme.branch]?.label} - ${
                        newsTheme.name
                      }`;
                  return (
                    <Select.Option key={newsTheme.id} value={newsTheme.id}>
                      <Tooltip title={label}>
                        <span>{label}</span>
                      </Tooltip>
                    </Select.Option>
                  );
                })}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={6} lg={4}>
          <Form.Item
            name="priority"
            key="priority"
            label={<span>{t("add_news.priority.select")}</span>}
            rules={[]}
          >
            <Select
              id="priority"
              placeholder={t("add_news.priority.placeholder")}
              allowClear
            >
              {NEWS_PRIORITY.map((priority: any) => (
                <Select.Option key={priority.value} value={priority.value}>
                  {t(`add_news.priority.${priority.label}`)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Form.Item
            name="creationRange"
            key="creationRange"
            label={<span>{t("news_list.search_creation_range")}</span>}
          >
            <RangePicker
              locale={locale}
              format={dateFormat}
              placeholder={[t("news.start_date"), t("news.end_date")]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Form.Item
            name="status"
            key="status"
            label={<span>{t("news.status")}</span>}
            rules={[]}
          >
            <Select id="status" placeholder={t("global.select")} allowClear>
              {Object.keys(NEWS_WORKFLOW_STATUS).map((status: any) => (
                <Select.Option key={status} value={status}>
                  {NEWS_WORKFLOW_STATUS[status].label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={7} lg={5}>
          <Form.Item
            name="archived"
            key="archived"
            label={<span>{t("news.archived")}</span>}
            rules={[]}
            initialValue={false}
          >
            <Select id="archived" placeholder={t("global.select")}>
              {NEWS_ARCHIVED.map((archived: any) => (
                <Select.Option key={archived.value} value={archived.value}>
                  {archived.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={9} lg={7}>
          <FormField
            labelName={t("news.id")}
            inputId="id"
            required={false}
            emptyErrorMessage={t("global.empty_error_field")}
            maxLength={FIELD_LENGTH.NAME}
          />
        </Col>
        <Col xs={24} sm={8} lg={6}>
          <Form.Item
            name="keyword"
            key="keyword"
            label={<span>{t("news.keywords.select")}</span>}
          >
            <Select
              id="keywords"
              className="wdio-keywords"
              options={getKeywordOptions()}
              allowClear
            />
          </Form.Item>
          {/* <FormField
            labelName={t("news.keywords.select")}
            inputId="keyword"
            required={false}
            emptyErrorMessage={t("global.empty_error_field")}
            maxLength={FIELD_LENGTH.NAME}
          /> */}
        </Col>
        <Col xs={24} sm={16} lg={12}>
          <FormField
            labelName={t("news.title")}
            inputId="title"
            required={false}
            emptyErrorMessage={t("global.empty_error_field")}
            maxLength={FIELD_LENGTH.TITLE}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default NewsListSearchForm;
